"use client";

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { Input, Select, Typography } from "@/components/ui";
import { NewButton } from "@/components/ui/NewButton";
import { env } from "@/lib/environment";
import { useGeoLocation } from "@/lib/hooks/useGeoLocation";
import { useUTMContext } from "@/lib/context/UTMProvider";
import {
  fireLeadCreated,
  postServerConversion,
  setChatGptAdsUser,
} from "@/lib/tracking/chatgptAds";
import CountryList from "country-list-with-dial-code-and-flag";
import { CalendlyWidget } from "@/components/CalendlyWidget";
import { trackDemoLead } from "@/lib/tracking/ga";

const sanitizePhoneNumber = (value: string) => value.replace(/\D/g, "");

const QUERY_OPTION_LABELS: Record<string, string> = {
  "pricing-and-plans": "Pricing & plans",
  "product-demo": "Product demo",
  "integrations-and-technical-fit": "Integrations & technical fit",
  "talk-to-sales": "Talk to sales",
  "something-else": "Something else",
};

const getQueryOptionLabel = (queryOption: string) =>
  QUERY_OPTION_LABELS[queryOption] ?? queryOption.replace(/-/g, " ");

const getHelpWithValue = (queryOption: string, otherDetails?: string) => {
  if (!queryOption) return undefined;
  if (queryOption === "something-else" && otherDetails?.trim()) {
    return otherDetails.trim();
  }
  return getQueryOptionLabel(queryOption);
};

interface FormData {
  fullName: string;
  email: string;
  country: string;
  phone: string;
  companyName: string;
  queryOption: string;
  screenCount: string;
  otherDetails?: string;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  queryOption?: string;
  screenCount?: string;
  otherDetails?: string;
}

export default function ContactForm() {
  const router = useRouter();
  const { geoData } = useGeoLocation();
  const { getAllTrackingData, getAttributionData } = useUTMContext();
  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    email: "",
    country: "US", // Default to US instead of IN
    phone: "",
    companyName: "",
    queryOption: "",
    screenCount: "",
    otherDetails: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [phoneValue, setPhoneValue] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<"US" | string>("US");
  const [selectedDialCode, setSelectedDialCode] = useState<string>("+1");
  const hasFiredLeadCreatedRef = useRef(false);
  const [calendlyUserData, setCalendlyUserData] = useState<{
    name: string;
    email: string;
    phone: string;
    company_name: string;
    screen_count: string;
    help_with?: string;
  } | null>(null);

  // Comprehensive country data with phone codes
  const countryData = useMemo(() => {
    const countries = CountryList.getAll();

    // Group countries by code to handle secondary dial codes
    const countryMap = new Map();

    countries.forEach(country => {
      const code = country.code;
      if (!countryMap.has(code)) {
        countryMap.set(code, {
          code: country.code,
          name: country.name,
          flag: country.flag,
          dialCodes: [],
        });
      }

      // Add dial code to the country's dial codes array
      if (country.dialCode) {
        countryMap.get(code).dialCodes.push(country.dialCode);
      }
    });

    // Convert to array and sort
    return Array.from(countryMap.values())
      .map(country => ({
        ...country,
        phoneCode: country.dialCodes[0] || "+1", // Use first dial code as default
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  // Get available dial codes for selected country
  const getAvailableDialCodes = useCallback(
    (countryCode: string) => {
      const country = countryData.find(c => c.code.toLowerCase() === countryCode.toLowerCase());
      return country?.dialCodes || ["+1"];
    },
    [countryData]
  );

  // Get current country data
  const currentCountry = useMemo(() => {
    return countryData.find(c => c.code.toLowerCase() === formData.country.toLowerCase());
  }, [countryData, formData.country]);

  // Auto-detect country from IP
  useEffect(() => {
    if (geoData?.countryCode) {
      const detectedCountry = geoData.countryCode.toLowerCase();
      setSelectedCountry(detectedCountry);
      setFormData(prev => ({ ...prev, country: detectedCountry }));

      // Set the first available dial code for the detected country
      const availableDialCodes = getAvailableDialCodes(detectedCountry);
      setSelectedDialCode(availableDialCodes[0]);
    }
  }, [geoData, countryData, getAvailableDialCodes]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    const sanitizedPhone = sanitizePhoneNumber(phoneValue);

    if (!sanitizedPhone) {
      newErrors.phone = "Phone number is required";
    } else if (sanitizedPhone.length < 4 || sanitizedPhone.length > 15) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    if (!formData.queryOption) {
      newErrors.queryOption = "Please select a query option";
    }

    if (!formData.screenCount) {
      newErrors.screenCount = "Please select number of screens";
    }

    // otherDetails is now optional
    // if (formData.queryOption === "other" && !formData.otherDetails?.trim()) {
    //   newErrors.otherDetails = "Please describe your query";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // If country changes, reset dial code to first available option
    if (name === "country") {
      const availableDialCodes = getAvailableDialCodes(value);
      setSelectedDialCode(availableDialCodes[0]);
    }

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Snapshot the current form values before any reset so the background
    // submission and Calendly prefill use consistent data.
    const sanitizedPhone = sanitizePhoneNumber(phoneValue);
    const fullPhone = `${selectedDialCode}${sanitizedPhone}`;
    const description =
      formData.otherDetails ||
      (formData.queryOption ? formData.queryOption.replace("-", " ") : "General Inquiry");
    const helpWith = getHelpWithValue(formData.queryOption, formData.otherDetails);
    const snapshot = { ...formData };

    setIsSubmitting(true);
    setSubmitStatus("idle");

    // Submit the lead first; Calendly opens only after the server confirms success.
    try {
      // Get reCAPTCHA token
      const grecaptcha = (window as any).grecaptcha;
      if (!grecaptcha || !grecaptcha.execute) {
        throw new Error("reCAPTCHA not loaded. Please refresh the page and try again.");
      }

      // Wait a bit for reCAPTCHA to be ready
      await new Promise(resolve => setTimeout(resolve, 100));

      const token = await grecaptcha.execute(env.NEXT_PUBLIC_GRECAPTCHA_KEY, { action: "submit" });

      if (!token) {
        throw new Error("Failed to get reCAPTCHA token. Please try again.");
      }

      // Prepare form details
      const details = {
        name: snapshot.fullName,
        email: snapshot.email,
        phone: fullPhone,
        company_name: snapshot.companyName,
        description,
        token: token,
        leads_source: "Website Contact",
        screen_count: snapshot.screenCount,
        form_name: "contact_us",
        submitted_at: new Date().toISOString(),
        address: {
          country_code: geoData?.countryCode,
          city: geoData?.city,
          region: geoData?.region,
        },
        ...getAllTrackingData(),
      };

      // Submit to API
      const res = await fetch(env.NEXT_PUBLIC_WIDGET_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          details: details,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();

      if (result.success) {
        setSubmitStatus("success");

        // Open Calendly only after the lead is confirmed saved,
        // so closing the popup/tab cannot lose the lead or conversion.
        setCalendlyUserData({
          name: snapshot.fullName,
          email: snapshot.email,
          phone: fullPhone,
          company_name: snapshot.companyName,
          screen_count: snapshot.screenCount,
          ...(helpWith ? { help_with: helpWith } : {}),
        });

        // Reset the form fields now that the lead is saved.
        setFormData({
          fullName: "",
          email: "",
          country: selectedCountry,
          phone: "",
          companyName: "",
          queryOption: "",
          screenCount: "",
          otherDetails: "",
        });
        setPhoneValue("");

        if (!hasFiredLeadCreatedRef.current) {
          hasFiredLeadCreatedRef.current = true;
          trackDemoLead();
          await setChatGptAdsUser(snapshot.email, fullPhone);
          const eventId = fireLeadCreated();
          postServerConversion({
            id: eventId,
            type: "lead_created",
            oppref: getAttributionData().oppref,
            sourceUrl: window.location.href,
            email: snapshot.email,
            phone: fullPhone,
          });
        }

        // // // Dispatch Mixpanel tracking event
        window.dispatchEvent(
          new CustomEvent("mixpanelEvent", {
            detail: {
              type: "contact-us-submitted",
              data: {
                email: snapshot.email,
                name: snapshot.fullName,
                country: snapshot.country,
                company_name: snapshot.companyName,
                phone: fullPhone,
                description,
                screen_count: snapshot.screenCount || "0",
              },
            },
          })
        );
      } else {
        throw new Error(result.message || "Submission failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col w-full lg:w-[476px] bg-white shadow-default rounded-sm">
      <form
        onSubmit={handleSubmit}
        className="w-full flex flex-col gap-4 py-8 px-6 md:py-7 md:px-9 rounded-lg"
      >
        <Input
          id="fullName"
          name="fullName"
          type="text"
          label="Full Name*"
          placeholder="Full Name"
          size="md"
          value={formData.fullName}
          onChange={handleInputChange}
          error={errors.fullName}
          required
        />

        <Input
          id="email"
          name="email"
          type="email"
          label="Business Email Address*"
          placeholder="Enter your business email"
          size="md"
          value={formData.email}
          onChange={handleInputChange}
          error={errors.email}
          required
        />

        <Input
          id="companyName"
          name="companyName"
          type="text"
          label="Company Name/Website*"
          placeholder="Enter website url "
          size="md"
          value={formData.companyName}
          onChange={handleInputChange}
          error={errors.companyName}
          required
        />

        {/* Phone Number */}
        <div className="flex flex-col gap-1.5">
          <label id="phone-label" className="text-sm font-medium text-text-primary">
            Phone Number*
          </label>
          <div className="flex flex-row w-full gap-2">
            {/* Country flag trigger with native select overlay showing names */}
            <div className="relative">
              <div className="w-10 h-11 rounded-lg border border-[#D7D9DE] bg-white flex items-center justify-center text-base">
                <span aria-hidden>{currentCountry?.flag || "🇺🇸"}</span>
              </div>
              <select
                id="country"
                name="country"
                aria-label="Country"
                value={formData.country}
                onChange={handleInputChange}
                className="absolute inset-0 w-14 h-12 opacity-0 cursor-pointer"
              >
                {countryData.map(country => (
                  <option key={country.code} value={country.code.toLowerCase()}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Phone Input with Country Code */}
            <div className="h-11 w-full rounded-lg border border-[#D7D9DE] bg-white px-4 flex items-center gap-2 text-lg text-text-secondary">
              {/* Dial Code Selector - only show if multiple options */}
              {currentCountry && currentCountry.dialCodes.length > 1 ? (
                <select
                  value={selectedDialCode}
                  onChange={e => setSelectedDialCode(e.target.value)}
                  className="text-text-primary bg-transparent outline-none border-none cursor-pointer text-sm md:text-base font-medium"
                >
                  {currentCountry.dialCodes.map((dialCode: string) => (
                    <option key={dialCode} value={dialCode}>
                      {dialCode}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-text-primary text-sm md:text-base font-medium">
                  {selectedDialCode}
                </span>
              )}
              <input
                id="phone"
                name="phone"
                type="tel"
                placeholder="Enter phone number"
                value={phoneValue}
                onChange={e => {
                  const value = e.target.value;
                  setPhoneValue(value);
                  setErrors(prev => {
                    if (!prev.phone) {
                      return prev;
                    }
                    return { ...prev, phone: undefined };
                  });
                }}
                className="w-full h-full bg-transparent outline-none text-text-primary placeholder:text-text-secondary/80 text-sm md:text-base font-medium"
                inputMode="tel"
                pattern="[0-9]*"
                autoComplete="tel"
                aria-labelledby="phone-label"
                // required
              />
            </div>
          </div>
          {errors.phone && <p className="text-sm text-red-500 mt-1">{errors.phone}</p>}
        </div>

        <Select
          label="How many screens do you plan to start with?*"
          id="screenCount"
          name="screenCount"
          aria-label="screens"
          rightIcon={
            <svg width="16" height="16" viewBox="0 0 20 20">
              <path d="M5 7l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
            </svg>
          }
          size="md"
          value={formData.screenCount}
          onChange={handleInputChange}
          error={errors.screenCount}
          required
        >
          <option value="" disabled>
            Please select number of screens
          </option>
          <option value="1-5">1 - 5</option>
          <option value="6-20">6 - 20</option>
          <option value="21-200">21 - 200</option>
          <option value="201+">201+</option>
        </Select>

        <Select
          label="What can we help you with?*"
          id="queryOption"
          name="queryOption"
          aria-label="Query"
          rightIcon={
            <svg width="16" height="16" viewBox="0 0 20 20">
              <path d="M5 7l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
            </svg>
          }
          size="md"
          value={formData.queryOption}
          onChange={handleInputChange}
          error={errors.queryOption}
          required
        >
          <option value="" disabled>
            Select one
          </option>
          <option value="pricing-and-plans">Pricing &amp; plans</option>
          <option value="product-demo">Product demo</option>
          <option value="integrations-and-technical-fit">Integrations &amp; technical fit</option>
          <option value="talk-to-sales">Talk to sales</option>
          <option value="something-else">Something else</option>
          {/* <option value="setup">I need help with setup</option> */}
        </Select>

        {formData.queryOption === "something-else" && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="otherDetails" className="text-sm font-medium text-text-primary">
              Please describe your query
            </label>
            <textarea
              id="otherDetails"
              name="otherDetails"
              aria-labelledby="otherDetails"
              placeholder="Type your message here"
              value={formData.otherDetails}
              onChange={handleInputChange}
              className="min-h-[96px] w-full rounded-md border border-gray-300 bg-white text-text-primary placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition p-4 text-sm md:text-base font-medium"
            />
            {errors.otherDetails && <p className="text-sm text-primary">{errors.otherDetails}</p>}
          </div>
        )}

        {/* Status Messages */}
        {submitStatus === "success" && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">
              Thank you! Your message has been sent successfully. We&apos;ll get back to you soon.
            </p>
          </div>
        )}

        {submitStatus === "error" && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">
              Sorry, there was an error sending your message. Please try again.
            </p>
          </div>
        )}

        <NewButton
          type="submit"
          variant="primary"
          className="w-full text-lg px-0"
          disabled={isSubmitting}
          size="lg"
        >
          {isSubmitting ? "Submitting..." : "Talk to our signage expert."}
        </NewButton>
      </form>
      <CalendlyWidget
        isOpen={calendlyUserData !== null}
        onClose={() => setCalendlyUserData(null)}
        calendlyUrl="https://calendly.com/d/dvqb-zrc-9b8/talk-to-our-experts"
        autoPopulate={calendlyUserData ?? undefined}
        preload
      />
    </div>
  );
}
